/**
 * Helpers for splitting a clinic's "headline" rich text between the hero and
 * the page body.
 *
 * The hero shows the first paragraph as its intro line, so the body must render
 * the same rich text with that paragraph removed, otherwise it appears twice.
 * Both helpers pick the same node, so they stay in sync.
 */

/**
 * Paragraphs that are page furniture rather than prose, and so make a poor
 * hero intro. Plano opens with "Medical Clinic Address: 5055 W Park Blvd...",
 * which was being used as its subtitle even though the clinic already has a
 * proper address field.
 */
const NOT_INTRO_COPY = [/\baddress\s*:/i, /\bclick (the )?(button|link)\b/i]

const isNonEmptyParagraph = node => {
  if (node.nodeType !== "paragraph") return false

  const text = (node.content || [])
    .map(child => child.value || "")
    .join("")
    .trim()

  return Boolean(text) && !NOT_INTRO_COPY.some(pattern => pattern.test(text))
}

/**
 * The hero's intro must come from the half of the field that describes the
 * centre, never from the insurance disclaimer below the rule. Some clinics have
 * no copy above the rule at all, and those were showing the disclaimer as their
 * hero subtext. Returning -1 leaves the hero without a subtitle instead.
 *
 * `before` is always a leading slice, so its indices match the full array and
 * getHeadlineWithoutIntro can remove the same node.
 */
const findIntroIndex = headline =>
  splitAtRule(headline).before.findIndex(isNonEmptyParagraph)

/** The first paragraph's plain text, used as the hero's intro line. */
export const getClinicIntro = headline => {
  const index = findIntroIndex(headline)
  if (index === -1) return ""

  return (headline.json.content[index].content || [])
    .map(child => child.value || "")
    .join("")
    .trim()
}

/** The same rich text with the hero's intro paragraph removed. */
export const getHeadlineWithoutIntro = headline => {
  const index = findIntroIndex(headline)
  if (index === -1) return headline

  const content = headline.json.content.filter((_, i) => i !== index)

  return { ...headline, json: { ...headline.json, content } }
}

/**
 * Clinic headlines are written as two halves separated by a horizontal rule:
 * what the centre offers, then the insurance disclaimer. `hr` is the divider.
 */
const withContent = (headline, content) =>
  content.length ? { ...headline, json: { ...headline.json, content } } : null

const hasText = node =>
  node.nodeType === "unordered-list" ||
  node.nodeType === "ordered-list" ||
  Boolean(nodeText(node))

/**
 * Find the rule that divides centre copy from the insurance disclaimer.
 *
 * Three shapes exist in Contentful and all three have to work:
 *
 *  - Carlsbad: copy, rule, disclaimer. Split at the rule.
 *  - Shrewsbury: a stray rule, then copy, then the real rule, then the
 *    disclaimer. Splitting on the first threw the whole entry away, so the
 *    divider is the first rule with content above it.
 *  - Dedham: a single leading rule and nothing but the disclaimer after it.
 *    There is no centre copy, so `before` must be empty. Falling through to
 *    "no rule found" here handed the disclaimer back as the intro.
 */
const splitAtRule = headline => {
  const nodes = headline?.json?.content || []
  const ruleIndices = nodes
    .map((node, i) => (node.nodeType === "hr" ? i : -1))
    .filter(i => i !== -1)

  if (!ruleIndices.length) return { before: nodes, after: [] }

  const divider = ruleIndices.find(i => nodes.slice(0, i).some(hasText))

  // Every rule is a leading one, so there is no centre copy at all.
  if (divider === undefined) {
    const last = ruleIndices[ruleIndices.length - 1]
    return { before: [], after: nodes.slice(last + 1) }
  }

  return { before: nodes.slice(0, divider), after: nodes.slice(divider + 1) }
}

const NEARBY_HEADING = /nearby area/i

const nodeText = node =>
  (node.content || [])
    .map(child => child.value || nodeText(child))
    .join("")
    .trim()

/**
 * The "Nearby Areas We Serve" heading and the list under it, pulled out so the
 * section can render them as a collapsible. Returns null when a clinic has not
 * been given that content.
 */
/**
 * Spot the "Nearby Areas We Serve" label above the list of towns.
 *
 * Most centres write it as a heading, but some write it as a plain paragraph
 * (Tempe, for one). Both count, otherwise the towns underneath are not
 * recognised and end up rendering as facility bullets instead.
 *
 * The length check keeps this to labels. A sentence that happens to mention
 * nearby areas is prose, not a heading for the list.
 */
const isNearbyLabel = node => {
  if (!node.nodeType.startsWith("heading") && node.nodeType !== "paragraph") {
    return false
  }

  const text = nodeText(node)

  return NEARBY_HEADING.test(text) && text.length <= 40
}

export const getNearbyAreas = headline => {
  const nodes = headline?.json?.content || []
  const headingIndex = nodes.findIndex(isNearbyLabel)
  if (headingIndex === -1) return null

  const areas = []
  for (let i = headingIndex + 1; i < nodes.length; i += 1) {
    const node = nodes[i]
    if (node.nodeType.startsWith("heading") || node.nodeType === "hr") break

    if (node.nodeType === "unordered-list") {
      ;(node.content || []).forEach(item => {
        const text = nodeText(item)
        if (text) areas.push(text)
      })
    } else {
      const text = nodeText(node)
      if (text) areas.push(text)
    }
  }

  return areas.length ? { title: nodeText(nodes[headingIndex]), areas } : null
}

/** What the centre offers, above the rule, minus the intro and nearby areas. */
export const getCentreHighlights = headline => {
  const withoutIntro = getHeadlineWithoutIntro(headline)
  if (!withoutIntro) return null

  const before = splitAtRule(withoutIntro).before
  const headingIndex = before.findIndex(isNearbyLabel)

  // Nearby areas render separately as a collapsible, so stop before them.
  return withContent(
    withoutIntro,
    headingIndex === -1 ? before : before.slice(0, headingIndex)
  )
}

/**
 * The heading that introduces a centre's facility list. Wording varies:
 * dev writes "The center offers:", production writes "Our {City} Center
 * Features:". Both are matched, and nothing else is.
 *
 * Matching on meaning rather than position matters. Clinic headlines also
 * carry promotional headings ("Coming soon!", "Melmed Center is now a part of
 * Cortica!"), and simply taking the first heading picked those up instead.
 */
const OFFERS_HEADING = /\b(offers|features|includes)\b/i

/**
 * Normalise a clinic's highlights into the shape the design asks for:
 * a lead-in paragraph, a facility-list heading, then a single bullet list.
 *
 * Clinics have been written inconsistently, some put every feature in the
 * list, others trail extra features on as loose paragraphs. Everything after
 * the heading is treated as a bullet so every centre renders the same way.
 *
 * Many centres have a facility list with no heading above it at all. Those
 * lists are still shown, just without a title, rather than being dropped.
 */
const isList = node =>
  node.nodeType === "unordered-list" || node.nodeType === "ordered-list"

const listItems = node => (node.content || []).map(nodeText).filter(Boolean)

export const getCentreOffer = headline => {
  const highlights = getCentreHighlights(headline)
  const nodes = highlights?.json?.content || []
  if (!nodes.length) return null

  const isHeading = node => node.nodeType.startsWith("heading")

  // Lead copy is whatever sits above the first heading of any kind, so
  // promotional headings and their body text stay out of it. Addresses and
  // "click the button below" lines are page furniture and are dropped, the
  // same as they are for the hero intro.
  const firstHeading = nodes.findIndex(isHeading)
  const lead = (firstHeading === -1 ? nodes : nodes.slice(0, firstHeading))
    .filter(isNonEmptyParagraph)
    .map(nodeText)

  const offersIndex = nodes.findIndex(
    node => isHeading(node) && OFFERS_HEADING.test(nodeText(node))
  )

  // No heading, but a list above the rule is still the facility list. Show it
  // untitled rather than discarding real content.
  if (offersIndex === -1) {
    const bare = nodes.filter(isList).flatMap(listItems)
    return { items: bare, lead, title: "" }
  }

  const items = []
  for (let i = offersIndex + 1; i < nodes.length; i += 1) {
    const node = nodes[i]
    // Stop at the next heading so a later section is not absorbed as bullets
    if (isHeading(node)) break

    if (
      node.nodeType === "unordered-list" ||
      node.nodeType === "ordered-list"
    ) {
      ;(node.content || []).forEach(item => {
        const text = nodeText(item)
        if (text) items.push(text)
      })
    } else {
      const text = nodeText(node)
      if (text) items.push(text)
    }
  }

  return { items, lead, title: nodeText(nodes[offersIndex]) }
}

/** The insurance disclaimer and anything else below the rule. */
export const getHeadlineRemainder = headline => {
  const withoutIntro = getHeadlineWithoutIntro(headline)
  if (!withoutIntro) return null

  return withContent(withoutIntro, splitAtRule(withoutIntro).after)
}
